import { useState } from 'react';
import { type Language, t } from '../i18n';
import type { FormData } from '../hooks/useChat';

interface LandingFormProps {
  language: Language;
  // `contactEmail` is optional at this stage — captured here saves the partial lead
  // even if the user abandons the chat before reaching the final lead form.
  onSubmit: (data: FormData, contactEmail?: string) => void;
}

const COMPANY_SIZES = ['1-9', '10-49', '50-199', '200-999', '1000-4999', '5000+'];
const REVENUE_RANGES = ['<€1M', '€1M-5M', '€5M-10M', '€10M-50M', '€50M-100M', '€100M-500M', '>€500M'];
const INDUSTRIES = [
  'Technology/SaaS',
  'Finance/Banking',
  'Retail/E-commerce',
  'Logistics/Supply Chain',
  'Healthcare',
  'Manufacturing',
  'Professional Services',
  'Energy/Utilities',
  'Government/Public Sector',
  'Tourism/Hospitality',
  'Construction',
  'Food/Agriculture',
  'Fashion/Textile',
];
const PRIMARY_GEOGRAPHIES = ['Italy', 'Europe', 'Global'];
const OTHER_GEOGRAPHIES = ['US', 'LATAM', 'MENA', 'East Europe', 'Eurasia', 'Asia', 'China'];
const SOFTWARE_TOOLS = ['ERP', 'CRM', 'Office', 'PM', 'Comms', 'Industry', 'Legacy', 'AI'];
const ROLES = ['CEO', 'C-Suite', 'VP', 'Manager', 'Analyst', 'Consultant'];

export function LandingForm({ language, onSubmit }: LandingFormProps) {
  const [formData, setFormData] = useState<FormData>({
    companySize: '',
    revenueRange: '',
    industry: '',
    geography: [],
    softwareTools: [],
    role: '',
    hasSoftwarePartner: undefined,
    itStructure: undefined,
    lookingForPartner: undefined,
    primaryChallenge: undefined,
    decisionTimeline: undefined,
    automationExperience: undefined,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, boolean>>>({});
  const [showOtherGeo, setShowOtherGeo] = useState(false);
  const [customGeo, setCustomGeo] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [emailError, setEmailError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<Record<keyof FormData, boolean>> = {};
    if (!formData.companySize) newErrors.companySize = true;
    if (!formData.industry) newErrors.industry = true;
    if (formData.geography.length === 0) newErrors.geography = true;
    if (!formData.role) newErrors.role = true;

    const trimmedEmail = contactEmail.trim();
    // Email is optional but if provided must be well-formed.
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData, trimmedEmail || undefined);
  };

  const handleCheckboxChange = (field: 'geography' | 'softwareTools', value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="relative mb-8 rounded-2xl overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-primary-50 dark:bg-primary-50/10" />

        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-100/60 rounded-full blur-3xl dark:bg-primary-100/10" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary-100/40 rounded-full blur-2xl dark:bg-primary-100/5" />

        {/* Content */}
        <div className="relative z-10 px-8 py-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {t(language, 'hero.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t(language, 'hero.subtitle')}
          </p>

          {/* Stats Row */}
          <div className="flex justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">{t(language, 'hero.stat1')}</div>
              <div className="text-sm text-muted-foreground">{t(language, 'hero.stat1Label')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">{t(language, 'hero.stat2')}</div>
              <div className="text-sm text-muted-foreground">{t(language, 'hero.stat2Label')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-primary">{t(language, 'hero.stat3')}</div>
              <div className="text-sm text-muted-foreground">{t(language, 'hero.stat3Label')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-6">
        <h2 className="font-semibold text-foreground mb-6 text-lg">
          {t(language, 'form.title')}
        </h2>

        {/* Section 1: Company Profile */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label htmlFor="companySize" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.companySize.label')} *
            </label>
            <select
              id="companySize"
              value={formData.companySize}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, companySize: e.target.value }));
                setErrors((prev) => ({ ...prev, companySize: false }));
              }}
              className={`form-select text-sm py-2 ${errors.companySize ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'form.companySize.placeholder')}</option>
              {COMPANY_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t(language, `form.companySize.options.${size}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="revenueRange" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.revenue.label')}
            </label>
            <select
              id="revenueRange"
              value={formData.revenueRange}
              onChange={(e) => setFormData((prev) => ({ ...prev, revenueRange: e.target.value }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.revenue.placeholder')}</option>
              {REVENUE_RANGES.map((range) => (
                <option key={range} value={range}>
                  {t(language, `form.revenue.options.${range}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="industry" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.industry.label')} *
            </label>
            <select
              id="industry"
              value={formData.industry}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, industry: e.target.value }));
                setErrors((prev) => ({ ...prev, industry: false }));
              }}
              className={`form-select text-sm py-2 ${errors.industry ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'form.industry.placeholder')}</option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {t(language, `form.industry.options.${industry}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="role" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.role.label')} *
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, role: e.target.value }));
                setErrors((prev) => ({ ...prev, role: false }));
              }}
              className={`form-select text-sm py-2 ${errors.role ? 'border-red-500' : ''}`}
            >
              <option value="">{t(language, 'form.role.placeholder')}</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(language, `form.role.options.${role}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Section 2: Context Fields - Primary Challenge & Experience */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <div>
            <label htmlFor="primaryChallenge" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.primaryChallenge.label')} *
            </label>
            <select
              id="primaryChallenge"
              value={formData.primaryChallenge ?? ''}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                primaryChallenge: e.target.value as 'data-entry' | 'reporting' | 'approvals' | 'communication' | 'inventory' | 'other' | undefined || undefined,
              }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.primaryChallenge.placeholder')}</option>
              <option value="data-entry">{t(language, 'form.primaryChallenge.options.data-entry')}</option>
              <option value="reporting">{t(language, 'form.primaryChallenge.options.reporting')}</option>
              <option value="approvals">{t(language, 'form.primaryChallenge.options.approvals')}</option>
              <option value="communication">{t(language, 'form.primaryChallenge.options.communication')}</option>
              <option value="inventory">{t(language, 'form.primaryChallenge.options.inventory')}</option>
              <option value="other">{t(language, 'form.primaryChallenge.options.other')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="decisionTimeline" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.decisionTimeline.label')}
            </label>
            <select
              id="decisionTimeline"
              value={formData.decisionTimeline ?? ''}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                decisionTimeline: e.target.value as 'exploring' | 'this-year' | 'urgent' | undefined || undefined,
              }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.decisionTimeline.placeholder')}</option>
              <option value="exploring">{t(language, 'form.decisionTimeline.options.exploring')}</option>
              <option value="this-year">{t(language, 'form.decisionTimeline.options.this-year')}</option>
              <option value="urgent">{t(language, 'form.decisionTimeline.options.urgent')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="automationExperience" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.automationExperience.label')}
            </label>
            <select
              id="automationExperience"
              value={formData.automationExperience ?? ''}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                automationExperience: e.target.value as 'none' | 'some' | 'advanced' | undefined || undefined,
              }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.automationExperience.placeholder')}</option>
              <option value="none">{t(language, 'form.automationExperience.options.none')}</option>
              <option value="some">{t(language, 'form.automationExperience.options.some')}</option>
              <option value="advanced">{t(language, 'form.automationExperience.options.advanced')}</option>
            </select>
          </div>
        </div>

        {/* Section 3: Sales Intelligence Fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-muted/30 rounded-lg border border-border">
          <div>
            <label htmlFor="itStructure" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.itStructure.label')}
            </label>
            <select
              id="itStructure"
              value={formData.itStructure ?? ''}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                itStructure: e.target.value as 'internal' | 'outsourced' | 'mixed' | 'none' | undefined || undefined,
              }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.itStructure.placeholder')}</option>
              <option value="internal">{t(language, 'form.itStructure.options.internal')}</option>
              <option value="outsourced">{t(language, 'form.itStructure.options.outsourced')}</option>
              <option value="mixed">{t(language, 'form.itStructure.options.mixed')}</option>
              <option value="none">{t(language, 'form.itStructure.options.none')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="hasSoftwarePartner" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.hasSoftwarePartner.label')}
            </label>
            <select
              id="hasSoftwarePartner"
              value={formData.hasSoftwarePartner ?? ''}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                hasSoftwarePartner: e.target.value as 'yes' | 'no' | 'looking' | undefined || undefined,
              }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.hasSoftwarePartner.placeholder')}</option>
              <option value="yes">{t(language, 'form.hasSoftwarePartner.options.yes')}</option>
              <option value="no">{t(language, 'form.hasSoftwarePartner.options.no')}</option>
              <option value="looking">{t(language, 'form.hasSoftwarePartner.options.looking')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="lookingForPartner" className="block text-xs font-medium text-foreground mb-1">
              {t(language, 'form.lookingForPartner.label')}
            </label>
            <select
              id="lookingForPartner"
              value={formData.lookingForPartner ?? ''}
              onChange={(e) => setFormData((prev) => ({
                ...prev,
                lookingForPartner: e.target.value as 'yes' | 'no' | 'better' | undefined || undefined,
              }))}
              className="form-select text-sm py-2"
            >
              <option value="">{t(language, 'form.lookingForPartner.placeholder')}</option>
              <option value="yes">{t(language, 'form.lookingForPartner.options.yes')}</option>
              <option value="no">{t(language, 'form.lookingForPartner.options.no')}</option>
              <option value="better">{t(language, 'form.lookingForPartner.options.better')}</option>
            </select>
          </div>
        </div>

        {/* Section 4: Geography */}
        <fieldset className="mb-4">
          <legend className="block text-xs font-medium text-foreground mb-2">
            {t(language, 'form.geography.label')} *
          </legend>
          <div className="flex flex-wrap gap-2">
            {PRIMARY_GEOGRAPHIES.map((geo) => (
              <label
                key={geo}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                  formData.geography.includes(geo)
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-card border-border text-foreground hover:border-primary/50'
                } ${errors.geography ? 'border-destructive' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={formData.geography.includes(geo)}
                  onChange={() => handleCheckboxChange('geography', geo)}
                  className="w-3.5 h-3.5 accent-primary rounded"
                />
                <span>{t(language, `form.geography.options.${geo}`)}</span>
              </label>
            ))}
            {/* Other toggle */}
            <label
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                showOtherGeo
                  ? 'bg-muted border-muted-foreground/50 text-foreground'
                  : 'bg-card border-border text-foreground hover:border-primary/50'
              } ${errors.geography ? 'border-destructive' : ''}`}
            >
              <input
                type="checkbox"
                checked={showOtherGeo}
                onChange={() => setShowOtherGeo(!showOtherGeo)}
                className="w-3.5 h-3.5 accent-primary rounded"
              />
              <span>{t(language, 'form.geography.other')}</span>
            </label>
          </div>
          {/* Expanded other regions */}
          {showOtherGeo && (
            <div className="mt-3 pl-4 border-l-2 border-border">
              <div className="flex flex-wrap gap-2">
                {OTHER_GEOGRAPHIES.map((geo) => (
                  <label
                    key={geo}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                      formData.geography.includes(geo)
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-card border-border text-foreground hover:border-primary/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.geography.includes(geo)}
                      onChange={() => handleCheckboxChange('geography', geo)}
                      className="w-3.5 h-3.5 accent-primary rounded"
                    />
                    <span>{t(language, `form.geography.options.${geo}`)}</span>
                  </label>
                ))}
                {/* Custom input */}
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={customGeo}
                    onChange={(e) => setCustomGeo(e.target.value)}
                    onBlur={() => {
                      if (customGeo.trim() && !formData.geography.includes(customGeo.trim())) {
                        handleCheckboxChange('geography', customGeo.trim());
                        setCustomGeo('');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customGeo.trim()) {
                        e.preventDefault();
                        if (!formData.geography.includes(customGeo.trim())) {
                          handleCheckboxChange('geography', customGeo.trim());
                        }
                        setCustomGeo('');
                      }
                    }}
                    placeholder={t(language, 'form.geography.customPlaceholder')}
                    className="px-3 py-1.5 text-sm border border-border rounded-md w-28 bg-card text-foreground focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          )}
        </fieldset>

        {/* Section 4: Software Tools */}
        <fieldset className="mb-6">
          <legend className="block text-xs font-medium text-foreground mb-2">
            {t(language, 'form.software.label')}
          </legend>
          <div className="flex flex-wrap gap-2">
            {SOFTWARE_TOOLS.map((tool) => (
              <label
                key={tool}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer transition-colors text-sm ${
                  formData.softwareTools.includes(tool)
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-card border-border text-foreground hover:border-primary/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.softwareTools.includes(tool)}
                  onChange={() => handleCheckboxChange('softwareTools', tool)}
                  className="w-3.5 h-3.5 accent-primary rounded"
                />
                <span>{t(language, `form.software.options.${tool}`)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {/* Optional contact email (captures partial leads) */}
        <fieldset>
          <label className="text-sm font-medium mb-1 block">
            {language === 'it' ? 'Email (opzionale)' : 'Email (optional)'}
          </label>
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              setEmailError(false);
            }}
            placeholder={language === 'it' ? 'tu@azienda.it' : 'you@company.com'}
            className={`form-input w-full ${emailError ? 'border-red-500' : ''}`}
          />
          <p className="text-xs text-muted mt-1">
            {language === 'it'
              ? 'Ti invieremo il report via email quando sarà pronto. Puoi anche lasciarla dopo.'
              : 'We\'ll send the report to this address when ready. You can also add it later.'}
          </p>
          {emailError && (
            <p className="text-xs text-red-500 mt-1">
              {language === 'it' ? 'Formato email non valido' : 'Invalid email format'}
            </p>
          )}
        </fieldset>

        {/* Submit */}
        <button type="submit" className="btn-primary w-full py-3 text-base font-medium">
          {t(language, 'form.submit')}
        </button>
      </form>
    </div>
  );
}
